import { Button, Card, Heading, IconButton, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type FormIdentifier, type IdentifierModel, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { selectFormById, selectFormIdentifiersOfForm } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateIdentifierDialog } from "../../components/Dialogs/CreateIdentifierDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/forms/$formId/identifiers")

export const FormSettingsIdentifiersView = memo(() => {
	const { formId } = routeApi.useParams()
	const form = useAppSelector(selectFormById(formId))

	if (!form) {
		throw new Error(`expected form to exist, got ${form}.`)
	}

	const formIdentifiers = useShallowEqaulSelector(selectFormIdentifiersOfForm(form.uuid))

	const handleSubmit = useCallback(
		(name: IdentifierModel["name"]) => {
			void sdk.formIdentifiers.add({
				form: form.uuid,
				name: name,
			})
		},
		[form.uuid],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Identifiers</Heading>
					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<CreateIdentifierDialog onSubmit={handleSubmit} size="sm">
							<Button variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Create
							</Button>
						</CreateIdentifierDialog>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<div role="list" className="flex flex-col gap-4">
					{formIdentifiers.length > 0 ? (
						formIdentifiers.map((formIdentifier) => {
							return <FormIdentifierListItem formIdentifier={formIdentifier} key={formIdentifier.uuid} />
						})
					) : (
						<Text size="sm" weight="regular" accentColor="base">
							No identifiers.
						</Text>
					)}
				</div>
			</section>
		</ViewContainer>
	)
})
FormSettingsIdentifiersView.displayName = "FormSettingsIdentifiersView"

interface FormIdentifierListItemProps {
	formIdentifier: Stored<FormIdentifier>
}

const FormIdentifierListItem = memo<FormIdentifierListItemProps>((props) => {
	const { formIdentifier } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handleFormIdentifierNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			void sdk.formIdentifiers.update({
				uuid: formIdentifier.uuid,
				name: value,
			})
		},
		[formIdentifier.uuid],
	)

	const formIdentifierNameInputProps = useBlurInput<HTMLInputElement>(
		formIdentifier.name,
		handleFormIdentifierNameInputBlur,
		true,
	)

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete identifier",
			description: "The identifier will be deleted immediately. This action cannot be undone.",
			onAction: () => sdk.formIdentifiers.delete(formIdentifier.uuid),
		})
	}, [formIdentifier.uuid, openConfirmDeleteDialog])

	return (
		<Card className="flex items-center justify-between gap-2" role="listitem" size="sm">
			<Input.Root variant="outline">
				<Input.Slot>
					<LuIcon icon={modelIconMapping.identifier} />
				</Input.Slot>
				<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...formIdentifierNameInputProps} />
			</Input.Root>

			<IconButton accentColor="base" size="sm" variant="ghost" disabled={!isAdmin} onClick={handleDelete}>
				<LuIcon icon={modelIconMapping.delete} />
			</IconButton>
		</Card>
	)
})
FormIdentifierListItem.displayName = "FormIdentifierListItem"
